import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getSystemBySlug } from '../data/systems';
import { getToolBySlug } from '../data/tools';
import FlowDiagram from '../components/FlowDiagram';

const FlowPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navBack = useNavigate();
  const system = slug ? getSystemBySlug(slug) : undefined;

  if (!system) {
    return (
      <div style={{ padding: '4rem 0', textAlign: 'center' }}>
        <div className="container">
          <h1>Flow Not Found</h1>
          <p>The flow you're looking for doesn't exist.</p>
          <Link to="/" className="button button-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 0 4rem' }}>
      <Helmet>
        <title>{system.name} — AI Workflow | ChooseAI</title>
        <meta name="description" content={system.metaDescription} />
        <meta property="og:title" content={`${system.name} | ChooseAI`} />
        <meta property="og:description" content={system.metaDescription} />
        <meta property="og:url" content={`https://chooseai.co.uk/flows/${system.slug}`} />
        <link rel="canonical" href={`https://chooseai.co.uk/flows/${system.slug}`} />
      </Helmet>

      <div className="container">
        {/* Back */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <button onClick={() => navBack(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.9rem', padding: 0, fontFamily: 'var(--font-primary)' }}>← Back</button>
        </div>

        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ display: 'inline-block', marginBottom: 'var(--space-md)' }}>
            <span className="badge badge-primary" style={{ fontSize: '0.8rem' }}>ChooseFlow</span>
          </div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: 'var(--space-md)' }}>{system.name}</h1>
          <p style={{ fontSize: '1.2rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-xl)' }}>
            {system.oneLiner}
          </p>

          {system.article && (
            <Link to={system.article} className="button button-primary" style={{ fontSize: '1rem', display: 'inline-block', marginTop: 'var(--space-sm)' }}>
              Read the Full Guide →
            </Link>
          )}
        </div>

        {/* Flow Diagram */}
        <div className="card" style={{ marginBottom: '2rem', padding: 'var(--space-xl)' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)' }}>The Flow</h3>
          <FlowDiagram nodes={system.nodes} edges={system.edges} />
        </div>

        {/* Inputs & Outputs */}
        <div className="grid grid-2" style={{ marginBottom: '2rem' }}>
          <div className="card">
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 'var(--space-xs)' }}>What goes in</span>
              Inputs
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {system.inputs.map((input, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)', fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>
                  <span style={{ color: 'var(--color-primary)', fontWeight: '700', flexShrink: 0 }}>→</span>
                  {input}
                </li>
              ))}
            </ul>
          </div>
          <div className="card">
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 'var(--space-xs)' }}>What comes out</span>
              Outputs
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {system.outputs.map((output, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)', fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>
                  <span style={{ color: 'var(--color-primary)', fontWeight: '700', flexShrink: 0 }}>→</span>
                  {output}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Description */}
        <div className="card markdown-content" style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)' }}>About This Flow</h3>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{system.description}</ReactMarkdown>
        </div>

        {/* Tool Cards */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)' }}>Tools in This Flow</h3>
          <div className="grid grid-3">
            {system.toolsUsed.map(slug => {
              const tool = getToolBySlug(slug);
              if (!tool) return null;
              return (
                <Link key={slug} to={`/tools/${slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <img
                      src={`/logos/${slug}.png`}
                      alt={`${tool.name} logo`}
                      style={{ width: '52px', height: '52px', borderRadius: '10px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '6px', margin: '0 auto var(--space-md) auto', border: '1px solid var(--color-border)' }}
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                    <h4 style={{ marginBottom: 'var(--space-sm)' }}>{tool.name}</h4>
                    <p style={{ fontSize: '0.875rem', marginBottom: 'var(--space-md)', flex: 1 }}>{tool.oneLiner}</p>
                    <span style={{ fontSize: '0.85rem', color: 'var(--color-primary)', fontWeight: '600' }}>View Profile →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowPage;
